<script>
import {useProductStore} from '@js/store/ProductStore.js'
import IconHeart from '@js/components/Icons/IconHeart.vue'
import IconInfo from '@js/components/Icons/IconInfo.vue'
import NumberFormatComponent from '@js/components/Formatter/NumberFormatComponent.vue'
import ProductCharacteristicsFeature from "@js/components/Sections/Product/ProductCharacteristicsFeature.vue";
import {useLikeStore} from "@js/store/LikeStore.js";
import IconHeartFilled from "@js/components/Icons/IconHeartFilled.vue";
import IconShare from "@js/components/Icons/IconShare.vue";
import {computed, onMounted, ref} from "vue";
import {useCartStore} from "@js/store/CartStore.js";
import QuantitySelector from "@js/components/Sections/Cart/QuantitySelector.vue";
import {Link} from "@inertiajs/vue3";
import {useCallbackStore} from "@js/store/CallbackStore.js";
import IconScales from "@js/components/Icons/IconScales.vue";
import {useCompareStore} from "@js/store/CompareStore.js";
import {numberCommaFormat} from '@js/helpers.ts';

export default {
    name: 'ProductPricingBlock',
    components: {
        IconScales,
        Link,
        QuantitySelector,
        IconShare,
        IconHeartFilled,
        ProductCharacteristicsFeature,
        IconHeart,
        IconInfo,
        NumberFormatComponent
    },
    setup() {
        const isClient = ref(false);

        const likeStore = useLikeStore()
        const compareStore = useCompareStore()

        const cartStore = useCartStore()
        const callbackStore = useCallbackStore()

        cartStore.loadCart()

        let productStore = useProductStore();
        const qty = ref(cartStore.calculateArea(productStore.product.length, productStore.product.width));

        const productFromCart = cartStore.findItem(productStore.product.id)

        let unit = ref('m2');
        if (productFromCart) {
            unit = ref(productFromCart.unit);
        }

        function onQtyChange(value) {
            qty.value = value;
        }

        function onUnitChange(value) {
            unit.value = value;
        }

        const unitType = computed(() => {
            return productStore.product.sale_by === 'pack' ? 'кор.' : 'м²'
        })

        onMounted(() => {
            isClient.value = true
        })

        return {
            productStore,
            likeStore,
            compareStore,
            cartStore,
            callbackStore,
            qty,
            unit,
            onQtyChange,
            onUnitChange,
            unitType,
            isClient,
            numberCommaFormat,
        }
    },
}
</script>

<template>
    <div class="flex flex-col gap-3">
        <div class="bg-grayBg rounded-2xl p-4 flex flex-col md:flex-row gap-5">
            <div class="w-full flex flex-col gap-1">
                <div class="flex items-center justify-between mb-2">
                    <div class="bg-gray-800 text-white text-xs font-medium px-2 py-1 rounded">
                        Артикул: {{ productStore.product.article ?? 'Не указан' }}
                    </div>
                    <div class="flex items-center space-x-3">
                        <IconScales @click.prevent="compareStore.toggleCompare(productStore.product.id)"
                                    :class="[compareStore.isCompared(productStore.product.id) ? 'text-blue-500 md:hover:text-gray-600' : 'text-gray-600 md:hover:text-blue-500']"
                                    class="w-5 h-5 cursor-pointer"/>

                        <IconHeart v-if="!likeStore.isLiked(productStore.product.id)"
                                   @click.prevent="likeStore.toggleLike(productStore.product.id)"
                                   class="w-5 h-5 text-gray-600 md:hover:text-red-600 cursor-pointer"/>
                        <IconHeartFilled v-else @click.prevent="likeStore.toggleLike(productStore.product.id)"
                                         class="w-5 h-5 text-red-600 md:hover:text-gray-600 cursor-pointer"/>

                        <IconShare class="w-4 h-4 text-gray-600 md:hover:text-baseColor cursor-pointer transition"/>
                    </div>
                </div>

                <ProductCharacteristicsFeature
                    v-if="productStore.product.area"
                    title="Площадь, м²"
                    :description="productStore.product.area"
                    bgColor="bg-grayBg"/>
                <ProductCharacteristicsFeature
                    v-if="productStore.product.area_in_box"
                    title="Площадь в коробке, м²"
                    :description="productStore.product.area_in_box"
                    bgColor="bg-grayBg"/>
                <ProductCharacteristicsFeature
                    v-if="productStore.product.count_in_box"
                    title="Количество в коробке, шт."
                    :description="productStore.product.count_in_box"
                    bgColor="bg-grayBg"/>
                <ProductCharacteristicsFeature
                    v-if="productStore.product.one_piece_weight"
                    title="Вес одной плитки, кг"
                    :description="productStore.product.one_piece_weight"
                    bgColor="bg-grayBg"/>
                <div class="text-nowrap text-sm leading-tight mt-3 mb-2">
                    <div v-if="productStore.product.price">
                        <NumberFormatComponent :number="productStore.product.price"
                                               class="text-3xl font-semibold"/>
                        руб./{{ unitType }}
                    </div>
                    <div v-else class="text-[20px] font-semibold">
                        Цена: Договорная
                    </div>
                </div>
                <QuantitySelector
                    v-if="productStore.product.price"
                    :key="productStore.product.id"
                    :product-id="productStore.product.id"
                    :model-value="cartStore.calculateArea(productStore.product.length, productStore.product.width)"
                    :model-unit="unit"
                    :area-per-piece="cartStore.calculateArea(productStore.product.length, productStore.product.width)"
                    :count-in-box="productStore.product.count_in_box"
                    @update:value="onQtyChange"
                    @update:unit="onUnitChange"/>
                <template v-if="isClient && productStore.product.price">
                    <button v-if="!cartStore.hasItem(productStore.product.id)"
                            @click.prevent="cartStore.addToCart(productStore.product, unit, qty)"
                            class="text-center mt-3 w-full bg-baseColor text-black py-3 rounded-xl md:hover:bg-baseColor/80 font-semibold">
                        В корзину
                    </button>
                    <Link v-else type="button" :href="route('cart')"
                          class="group text-center mt-3 w-full bg-emerald-600 text-white py-3 rounded-xl md:hover:bg-emerald-600/80 font-semibold">
                        <span class="group-hover:hidden">
                            Добавлено в корзину
                        </span>
                        <span class="hidden group-hover:block">
                            К корзине
                        </span>
                    </Link>
                </template>
                <template v-else>
                    <button type="button" @click.prevent="callbackStore.showBaseCallbackPopup({
                                                title: 'Мы предложим лучшую цену',
                                                bitrix_title: 'Заявка на уточнение цены',
                                                comment: 'Продукт: ' + productStore.product.title,
                                                img_src: productStore.images?.[0] ?? null,
                                            })"
                            class="text-center mt-3 w-full bg-baseColor text-black py-3 rounded-xl md:hover:bg-baseColor/80 font-semibold">
                        Узнать цену
                    </button>
                </template>
            </div>
        </div>
        <div class="bg-grayBg rounded-2xl p-4">
            <div class="w-full">
                <h3 class="text-lg font-semibold mb-2">Нашли дешевле?</h3>
                <div class="flex items-center text-gray-700">
                    <span class="text-[13px] font-medium">
                        Пришлите нам ссылку на этот товар в другом магазине, и в течение 24-х часов вы получите письмо на указанный вами E-mail с персональным предложением.
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
/* Ваши стили */
</style>
